import React from 'react'
import { createPortal } from 'react-dom'
import { splitAdvertiseLinks } from '../../../../functions/agent/advertises/portalLinks'
import { swalSuccess } from '../../../../functions/swalOptions'
import { useAppDispatch } from '../../../../hooks/redux'
import { updateManualAdvertisementPortalLinks } from './manualAdvertisementActions'
import { mapAdvertiseLinksToManualPortalLinks } from './manualAdvertisementPayload'
import { MANUAL_ADVERTISEMENT_PORTAL_OPTIONS } from './manualOperations.config'
import { type ManualAdvertisementPortal, type ManualAdvertisementPortalLink } from './manualAdvertisement.types'
import './ManualAdvertisementModal.scss'

const createPortalLink = (): ManualAdvertisementPortalLink => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    portal: '',
    link: '',
    comment: '',
    disabled: false,
    status: 'Approved'
})

const getTrakheesiLabel = (advertise: any): string =>
    splitAdvertiseLinks(advertise?.attributes?.Links)
        .trakheesi
        .map((link) => link.Trakheesi_id)
        .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        .join(', ')

export const ManualAdvertisementPortalLinksModal = ({
    advertise,
    onClose
}: {
    advertise: any | null
    onClose: () => void
}): JSX.Element | null => {
    const dispatch = useAppDispatch()
    const [trakheesiId, setTrakheesiId] = React.useState('')
    const [portalLinks, setPortalLinks] = React.useState<ManualAdvertisementPortalLink[]>([createPortalLink()])
    const [saving, setSaving] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        if (advertise == null) return
        const mappedLinks = mapAdvertiseLinksToManualPortalLinks(advertise)
        setTrakheesiId(getTrakheesiLabel(advertise))
        setPortalLinks(mappedLinks.length > 0 ? mappedLinks : [createPortalLink()])
        setSaving(false)
        setError(null)
    }, [advertise])

    React.useEffect(() => {
        if (advertise == null) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !saving) onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [advertise, saving, onClose])

    if (advertise == null || typeof document === 'undefined') return null

    const usablePortalLinks = portalLinks.filter((link) => link.sourceIndex != null || link.portal.trim() !== '' || link.link.trim() !== '')
    const invalidPortalLink = usablePortalLinks.find((link) => !link.disabled && (link.portal.trim() === '' || link.link.trim() === '' || !/^https?:\/\//i.test(link.link.trim())))
    const validationError = invalidPortalLink != null ? 'Each active portal link needs a portal name and http(s) URL.' : null
    const submitDisabled = saving || validationError != null
    const visibleError = error ?? validationError

    const updatePortalLink = (id: string, patch: Partial<ManualAdvertisementPortalLink>): void => {
        setPortalLinks((prev) => prev.map((link) => link.id === id ? { ...link, ...patch, status: 'Approved' } : link))
        setError(null)
    }

    const removePortalLink = (id: string): void => {
        setPortalLinks((prev) => {
            const target = prev.find((link) => link.id === id)
            if (target?.sourceIndex != null) {
                return prev.map((link) => link.id === id ? { ...link, disabled: true } : link)
            }
            const next = prev.filter((link) => link.id !== id)
            return next.length > 0 ? next : [createPortalLink()]
        })
        setError(null)
    }

    const handleSubmit = async (): Promise<void> => {
        if (submitDisabled) {
            setError(validationError)
            return
        }

        setSaving(true)
        setError(null)
        const result = await dispatch(updateManualAdvertisementPortalLinks({
            advertise,
            portalLinks: usablePortalLinks,
            trakheesiId
        }))

        if (result.isSuccessful) {
            setSaving(false)
            onClose()
            void swalSuccess('Portal links have been updated.', undefined, 'Success')
            return
        }

        setSaving(false)
        setError(result.error ?? 'Could not update portal links.')
    }

    return createPortal(
        <div className="manual-advertisement__backdrop" onClick={saving ? undefined : onClose}>
            <div className="manual-advertisement__modal manual-advertisement__modal--portal-links" role="dialog" aria-modal="true" aria-labelledby="manual-advertisement-portal-links-title" onClick={(event) => event.stopPropagation()}>
                <header className="manual-advertisement__header">
                    <div>
                        <h2 id="manual-advertisement-portal-links-title">Edit Portal Links</h2>
                        <p>Update portal links for advertisement #{String(advertise.id)}.</p>
                    </div>
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Close</button>
                </header>

                <div className="manual-advertisement__body">
                    <section className="manual-advertisement__panel">
                        <h3>Trakheesi</h3>
                        <div>
                            <label className="form-label" htmlFor="manual-advertisement-edit-trakheesi">Trakheesi ID</label>
                            <input id="manual-advertisement-edit-trakheesi" className="form-control form-control-sm" type="text" value={trakheesiId} disabled={saving} placeholder="253479128002" onChange={(event) => { setTrakheesiId(event.target.value); setError(null) }} />
                        </div>
                    </section>

                    <section className="manual-advertisement__panel">
                        <div className="manual-advertisement__panel-head">
                            <h3>Portal Links</h3>
                            <button className="btn btn-sm btn-light" type="button" disabled={saving} onClick={() => setPortalLinks((prev) => [...prev, createPortalLink()])}>
                                Add portal link
                            </button>
                        </div>

                        <div className="manual-advertisement__portal-list">
                            {portalLinks.map((portalLink, index) => {
                                const isExisting = portalLink.sourceIndex != null
                                const isDisabled = portalLink.disabled
                                return (
                                    <div className={`manual-advertisement__portal-row${isDisabled ? ' manual-advertisement__portal-row--disabled' : ''}`} key={portalLink.id}>
                                        <div className="manual-advertisement__portal-row-head">
                                            <strong>Portal #{index + 1}</strong>
                                            <div className="manual-advertisement__portal-actions">
                                                {portalLink.status != null && <span className="badge badge-light">{portalLink.status}</span>}
                                                <button className="btn btn-sm btn-light" type="button" disabled={saving} onClick={() => removePortalLink(portalLink.id)}>
                                                    {isExisting ? 'Disable' : 'Remove'}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="manual-advertisement__form-grid">
                                            <div>
                                                <label className="form-label" htmlFor={`manual-advertisement-edit-portal-${portalLink.id}`}>Portal name</label>
                                                <select id={`manual-advertisement-edit-portal-${portalLink.id}`} className="form-select form-select-sm" value={portalLink.portal} disabled={saving || isDisabled} onChange={(event) => updatePortalLink(portalLink.id, { portal: event.target.value as ManualAdvertisementPortal })}>
                                                    <option value="">Select portal</option>
                                                    {MANUAL_ADVERTISEMENT_PORTAL_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="form-label" htmlFor={`manual-advertisement-edit-link-${portalLink.id}`}>Portal link</label>
                                                <input id={`manual-advertisement-edit-link-${portalLink.id}`} className="form-control form-control-sm" type="url" value={portalLink.link} disabled={saving || isDisabled} placeholder="https://..." onChange={(event) => updatePortalLink(portalLink.id, { link: event.target.value })} />
                                            </div>
                                        </div>
                                        <label className="form-label" htmlFor={`manual-advertisement-edit-comment-${portalLink.id}`}>Comment</label>
                                        <textarea id={`manual-advertisement-edit-comment-${portalLink.id}`} className="form-control form-control-sm" rows={2} value={portalLink.comment} disabled={saving || isDisabled} placeholder="Optional comment for admin review" onChange={(event) => updatePortalLink(portalLink.id, { comment: event.target.value })} />
                                        <label className="manual-advertisement__checkbox">
                                            <input type="checkbox" checked={portalLink.disabled} disabled={saving} onChange={(event) => updatePortalLink(portalLink.id, { disabled: event.target.checked })} />
                                            Disabled
                                        </label>
                                        {isDisabled && <small className="text-muted">Disabled links are kept for history.</small>}
                                    </div>
                                )
                            })}
                        </div>
                    </section>

                    {visibleError != null && (
                        <div className="alert alert-light-danger mb-0">{visibleError}</div>
                    )}
                </div>

                <footer className="manual-advertisement__footer">
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn btn-sm btn-primary" type="button" onClick={() => { void handleSubmit() }} disabled={submitDisabled}>
                        {saving ? 'Saving...' : 'Save Portal Links'}
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    )
}
